/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useMemo, useState } from 'react';
import { SectionList, Text, TouchableOpacity, View } from 'react-native';
import { spacing } from '../../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { ModuleEmptyState } from '../../../shared/ui/ModuleEmptyState';
import { PaperSheet } from '../../../shared/ticket/PaperSheet'
import type { CashSessionRow } from '../../infra/cashSessionsRepo';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { CashHistorialFiltersBlock } from './CashHistorialFiltersBlock';
import { CashHistoryPagination } from './CashHistoryPagination';
import { CashSessionDetailPanel } from './CashDetailPanels';
import { HistorySessionRow } from './CashHistorialList';
import { buildHistorySections } from './cashManagement.utils';
import type { CashVm, HistorySection } from './cashManagement.types';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { CashWorkspaceTabPane } from './CashWorkspaceTabPane';
import { cashStyles } from './cashManagement.styles';

type Props = {
  vm: CashVm;
  onOpenCustomModal: () => void;
  layoutWide: boolean;
  /** Mobile: resumen + modal de filtros. */
  compactFilters?: boolean;
  onOpenFiltersModal?: () => void;
};

export function CashHistorialAuditPanel({
  vm,
  onOpenCustomModal,
  layoutWide: _layoutWide,
  compactFilters,
  onOpenFiltersModal,
}: Props) {
  const [detailSession, setDetailSession] = useState<CashSessionRow | null>(null);
  const sections = useMemo(
    () => buildHistorySections(vm.cashHistory, vm.historySort),
    [vm.cashHistory, vm.historySort]
  );

  const resetDetail = useCallback(() => {
    setDetailSession(null);
  }, []);

  const renderSectionHeader = useCallback(
    ({ section }: { section: HistorySection }) =>
      section.title === '' ? null : (
        <Text style={panelStyles.closedListHeader}>{section.title}</Text>
      ),
    []
  );

  const renderItem = useCallback(
    ({ section, item, index }: { section: HistorySection; item: CashSessionRow; index: number }) => {
      const isLast = index === section.data.length - 1;
      const group: 'grouped' | 'groupedLast' = isLast ? 'groupedLast' : 'grouped';
      return (
        <HistorySessionRow
          s={item}
          onPress={() => setDetailSession(item)}
          operatorLabel={vm.resolveOperatorLabel}
          group={group}
          selected={detailSession?.id === item.id}
        />
      );
    },
    [detailSession?.id, vm.resolveOperatorLabel]
  );

  const errorState = vm.historyError ? (
    <ModuleEmptyState
      variant="nest"
      title="No pudimos cargar el historial"
      subtitle="Revisá la conexión y probá de nuevo."
      showMascot={false}
      secondaryAction={{
        label: 'Reintentar',
        onPress: () => void vm.reloadHistoryFromStart(),
        testID: 'caja.history.retry',
      }}
      testID="caja.history.error"
    />
  ) : null;

  const emptyState =
    vm.historyLoading && vm.cashHistory.length === 0 ? (
      <ModuleEmptyState
        loading
        loadingText="Cargando historial…"
        testID="caja.history.empty.loading"
      />
    ) : vm.historyError ? null : !vm.historyLoading && vm.cashHistory.length === 0 ? (
      <ModuleEmptyState
        variant="nest"
        title={
          vm.differenceFilter !== 'all' || vm.operatorFilter !== 'all'
            ? 'Sin turnos con estos filtros'
            : 'Sin cierres en este período'
        }
        subtitle={
          vm.lastClosedOutsideHistoryRange && vm.cashSummary?.lastClosedSession?.closedAt
            ? `El último cierre fue el ${formatLocalDateTime(vm.cashSummary.lastClosedSession.closedAt)}. Probá 90 días o un rango personalizado.`
            : 'Ampliá el rango de fechas o ajustá los filtros.'
        }
        kicker="Siguiente paso"
        testID="caja.history.empty"
      />
    ) : null;

  const filters = (
    <CashHistorialFiltersBlock
      vm={vm}
      onOpenCustomModal={onOpenCustomModal}
      workspaceMode
      compact={compactFilters}
      onOpenFiltersModal={onOpenFiltersModal}
    />
  );

  if (detailSession) {
    return (
      <View style={cashStyles.historialSingleColumn}>
        <CashWorkspaceTabPane wrapStyle={{ flex: 1, minHeight: 0 }}>
          <PaperSheet style={{ flex: 1, minHeight: 0 }} testID="caja.history.detail.sheet">
            <View style={cashStyles.historialDetailFullWidth}>
              <CashSessionDetailPanel
                s={detailSession}
                onClose={resetDetail}
                workspaceMode
                branchId={vm.currentBranchId ?? null}
                operatorLabel={vm.resolveOperatorLabel}
              />
              <TouchableOpacity style={styles.secondaryButton} onPress={resetDetail} accessibilityRole="button">
                <Text style={styles.secondaryButtonText}>Volver al historial</Text>
              </TouchableOpacity>
            </View>
          </PaperSheet>
        </CashWorkspaceTabPane>
      </View>
    );
  }

  const listFooter = (
    <View style={panelStyles.historialCardFooter}>
      <CashHistoryPagination vm={vm} />
    </View>
  );

  const sectionList = (
    <SectionList
      {...webOperationalScrollBindings(cashStyles.historialListFullWidth)}
      sections={sections}
      keyExtractor={(item) => item.id}
      renderItem={renderItem}
      renderSectionHeader={renderSectionHeader}
      keyboardShouldPersistTaps="handled"
      stickySectionHeadersEnabled={false}
      ListHeaderComponent={errorState}
      ListFooterComponent={listFooter}
      ListEmptyComponent={emptyState}
      contentContainerStyle={[
        panelStyles.sectionListContentWorkspace,
        vm.cashHistory.length === 0 ? { flexGrow: 1 } : null,
      ]}
      nestedScrollEnabled
    />
  );

  return (
    <View style={cashStyles.historialSingleColumn}>
      <CashWorkspaceTabPane
        caption="Cierres de caja de la sucursal"
        wrapStyle={{ flex: 1, minHeight: 0 }}
      >
        {filters}
        <PaperSheet style={{ flex: 1, minHeight: 0 }} testID="caja.history.list.sheet">
          {sectionList}
        </PaperSheet>
      </CashWorkspaceTabPane>
    </View>
  );
}
